import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { ButtonComponent } from '../../../../shared/ui';
import { type AccountMailChannel, AccountMailCooldownStore } from '../../data/account-mail-cooldown.store';

/**
 * Botón de correo de la cuenta con espera (B24/B43 de B10): «Reenviar correo de verificación» en la cabecera y en
 * ajustes, «Enviar enlace» en ajustes. La espera es la del canal en `AccountMailCooldownStore`, compartida entre todos
 * los botones del mismo canal; mientras dura, el botón está deshabilitado con `waitLabel` («Reenviar en {s} s»).
 *
 * Solo emite `send`: quien lo monta hace la petición con `store.send()` y pinta el aviso. Sin timer propio: el reloj
 * es del store y se para solo.
 */
@Component({
  selector: 'app-mail-cooldown-button',
  imports: [ButtonComponent],
  template: `
    <app-button
      variant="secondary"
      size="sm"
      [disabled]="waiting()"
      [loading]="busy()"
      (click)="onClick()"
    >
      {{ text() }}
    </app-button>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
})
export class MailCooldownButtonComponent {
  private readonly store = inject(AccountMailCooldownStore);

  readonly channel = input.required<AccountMailChannel>();
  readonly label = input.required<string>();
  /** Plantilla con `{s}`, los segundos que quedan. */
  readonly waitLabel = input.required<string>();

  readonly send = output<void>();

  protected readonly remaining = computed(() => this.store.remaining(this.channel())());
  protected readonly busy = computed(() => this.store.busy(this.channel())());
  protected readonly waiting = computed(() => this.remaining() > 0);

  protected readonly text = computed(() =>
    this.waiting() ? this.waitLabel().replace('{s}', String(this.remaining())) : this.label(),
  );

  protected onClick(): void {
    if (this.waiting() || this.busy()) return;
    this.send.emit();
  }
}
